/**
 * Utilidades para mejorar la experiencia de usuario en inputs numéricos
 */

/**
 * Handler para seleccionar todo el texto al hacer focus en un input.
 * Mejora la UX permitiendo reemplazar rápidamente el valor.
 * 
 * Combina onFocus con onMouseUp para evitar conflictos en inputs controlados de React.
 * 
 * Uso:
 *   <Input 
 *     onFocus={handleNumericInputFocus} 
 *     onMouseUp={handleNumericInputMouseUp}
 *   />
 */
export const handleNumericInputFocus = (e) => {
  const input = e.target;
  
  // Seleccionar todo el contenido al hacer focus
  if (input && typeof input.select === 'function') {
    input.select();
  }
};

/**
 * Handler para prevenir el comportamiento por defecto del mouseUp
 * que puede deseleccionar el texto en inputs controlados.
 * 
 * Uso:
 *   <Input 
 *     onFocus={handleNumericInputFocus} 
 *     onMouseUp={handleNumericInputMouseUp}
 *   />
 */
export const handleNumericInputMouseUp = (e) => {
  // Prevenir que el click deseleccione el texto
  e.preventDefault();
};

/**
 * Props comunes para inputs numéricos que mejoran la UX.
 * Incluye:
 * - Selección automática al hacer focus
 * - InputMode numérico para móviles
 * - Step para decimales
 * 
 * Uso:
 *   <Input {...getNumericInputProps()} />
 */
export const getNumericInputProps = (options = {}) => {
  const {
    allowDecimals = true,
    step = allowDecimals ? '0.01' : '1',
    min = undefined,
    inputMode = 'numeric'
  } = options;

  return {
    type: 'number',
    step,
    min,
    inputMode,
    onFocus: handleNumericInputFocus
  };
};

/**
 * Handler para permitir que un input numérico acepte valores vacíos temporalmente.
 * Útil para inputs controlados donde el usuario debe poder borrar todo el valor.
 * 
 * Uso:
 *   <Input 
 *     value={value}
 *     onChange={(e) => setValue(parseNumericInput(e.target.value))}
 *   />
 */
export const parseNumericInput = (value) => {
  // Permitir vacío y valores numéricos válidos
  if (value === '' || value === null || value === undefined) {
    return '';
  }
  
  const parsed = parseFloat(value);
  return isNaN(parsed) ? '' : parsed;
};

/**
 * Hook personalizado para manejar inputs numéricos con mejor UX.
 * Maneja el estado y la lógica de parsing automáticamente.
 * 
 * Uso:
 *   const [value, handleChange, inputProps] = useNumericInput(0);
 *   <Input value={value} onChange={handleChange} {...inputProps} />
 */
export const useNumericInput = (initialValue = 0, options = {}) => {
  const [value, setValue] = React.useState(initialValue);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    
    // Permitir vacío mientras edita
    if (newValue === '') {
      setValue('');
      return;
    }
    
    // Parsear y actualizar
    const parsed = parseFloat(newValue);
    if (!isNaN(parsed)) {
      setValue(parsed);
    }
  };
  
  const inputProps = getNumericInputProps(options);
  
  return [value, handleChange, inputProps];
};
